import {
	RMQR_VERSIONS,
	SYMBOLOGIES,
	barHeightFloor,
	checkPayload,
	defaultQuiet,
	moduleFloor,
} from './symbologies';
import { qrValue } from '../shareUrls';

/**
 * One symbol, encoded to SVG at a real physical size.
 *
 * THE WHOLE POINT IS THE ARITHMETIC. At `scale: 1` bwip-js emits an SVG
 * whose viewBox is the symbol's own module grid — measured here, encoder by
 * encoder, because the factor is not the same for every family:
 *
 *   matrix  (QR, Micro QR, rMQR, Data Matrix, Aztec)  TWO units per module
 *           (verified: a version-1 QR is 21 modules and emits a 42x42
 *           viewBox; a 16x16 Data Matrix emits 32x32; a one-layer compact
 *           Aztec is 15 modules and emits 30x30)
 *   linear and stacked                                 ONE unit per module
 *           (verified: Code 128 of seven characters is 11x9 + 13 = 112
 *           modules and emits a 112-wide viewBox; a four-column PDF417 is
 *           17x(4+4) + 1 = 137 and emits 137)
 *
 * So the printed size is
 *
 *     width  = (viewBoxWidth / unitsPerModule + 2 x quiet) x X-dimension
 *
 * in the page's own unit, and setting the <svg>'s CSS width to that in `in`
 * / `mm` rides the platform's existing CSS-inch model straight to the
 * printer. No scale factor is ever involved and nothing here is in pixels,
 * which is what makes the size testable as arithmetic rather than as a
 * screenshot.
 *
 * HEIGHT SPLITS BY FAMILY, because the standards do. A matrix or stacked
 * symbol's height is part of its encoding and is derived the same way its
 * width is; the symbol is never stretched. A LINEAR symbol's bar height
 * carries no data at all — it is the sweep tolerance — so it takes the
 * block's own box height and the bars stretch to fill it, which is what an
 * author resizing a Code 128 on a label actually means. (On a quarter turn
 * that is the block's WIDTH, because the bars now run the other way.)
 *
 * AND THE BOX IS A LIMIT. A symbol WIDER than its block comes back as an
 * error, not as something CSS resolves. The write-time floor in keepr-api
 * only ever sees `module`, never the module COUNT — which depends on the
 * item — so a template that fits the author's preview record overflows on
 * the long values later in the run; and both things CSS would do with the
 * overflow are silent. Shrinking prints below the X-dimension the API just
 * refused; clipping prints half a symbol. Both look like a barcode.
 *
 * FAILURE IS A RETURNED VALUE, NOT A THROW. A payload that cannot be
 * encoded — an EAN-13 of the wrong length, lower case in a Code 39, a
 * template that resolved to nothing — comes back as `{ error }`, so the
 * renderer can draw an inline notice in that block's own box and the rest of
 * the sheet still prints. A thrown encoder taking down a whole print route
 * is the regression this shape exists to prevent.
 *
 * THE ENCODER IS LAZY. `import('./encoders')` keeps roughly 120 KB gzipped
 * of Reed-Solomon and compaction tables out of the main bundle; it lands in
 * its own chunk that is fetched the first time a page layout with a barcode
 * on it is drawn.
 */

let encodersPromise = null;

/** The encoders, loaded once and shared. Exported for tests to reset. */
export function loadEncoders() {
	if (!encodersPromise) {
		encodersPromise = import('./encoders').catch(e => {
			// A failed chunk fetch must not poison every later attempt: a
			// reader who was briefly offline should get a symbol on their
			// next print, not a permanently broken block.
			encodersPromise = null;
			throw e;
		});
	}
	return encodersPromise;
}

export function resetEncoders() {
	encodersPromise = null;
}

/**
 * GS1 payloads are a GRAMMAR, not a string, and the grammar has NO ESCAPE.
 *
 * The gs1* symbologies read `(01)...(17)...` application-identifier syntax
 * out of the payload, so a stray parenthesis in somebody's asset tag can
 * rewrite the AI stream. What BWIPP will accept INSIDE an AI's data is
 * CSET 82 — digits, letters and twenty punctuation marks — and nothing
 * else: anything outside it is `GS1badCSET82character`, which is a red
 * notice where a label should be.
 *
 * SO THE ANSWER IS SANITISATION, NOT ESCAPING. A backslash is not an escape
 * here, it is simply a character outside CSET 82: `(10)AB\)C` is refused
 * where `(10)AB)C` encodes fine, so backslash-escaping turns working labels
 * into errors and still does not rescue the one case that needs rescuing —
 * `(10)AB(C` is `GS1aiMissingCloseParen` whether the paren is escaped or
 * not, because `(` starts a new AI.
 *
 * Both parentheses go, therefore, along with everything outside CSET 82.
 * The AI skeleton belongs to the template author; an interpolated VALUE
 * brings only characters an AI can carry.
 *
 * (Verified against bwip-js 4.11.4's gs1_128: every one of the 82
 * characters below encodes, `(` alone does not, and `^ # { [` and space
 * — none of them in CSET 82 — are refused.)
 */
const GS1_CSET82 = "!\"%&'()*+,-./0123456789:;<=>?ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz";
const GS1_ALLOWED = new Set([...GS1_CSET82].filter(ch => ch !== '(' && ch !== ')'));

export const sanitizeGs1Value = value => [...String(value == null ? '' : value)]
	.filter(ch => GS1_ALLOWED.has(ch))
	.join('');

/** Is this symbology one whose payload is AI syntax? */
export const isGs1 = id => !!(SYMBOLOGIES[id] && SYMBOLOGIES[id].gs1);

const num = value => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * The bwip-js options for one block.
 *
 * ONE TABLE PER SYMBOLOGY, NOT ONE FLAT TABLE. The stored `code` vocabulary
 * is the AUTHOR's; BWIPP's option names are the ENCODER's; and the mapping
 * between them is not a function of the key alone. `eclevel` is a letter on
 * a QR and a percentage on an Aztec. `version` is an integer on a full QR,
 * 'M2' on a Micro QR, 'R13x99' on an rMQR and '16x16' on a Data Matrix.
 * `checkdigit` is `includecheck` alone on a Code 39 and `includecheck` plus
 * `checktype` on an MSI. A flat table gets four of those wrong at once —
 * some as a thrown `invalidOptionType` that draws a red notice, some as an
 * option BWIPP never reads, which is worse: the label prints, beautifully,
 * carrying a different number than the author configured.
 *
 * Every mapping below was checked by CALLING the shipped encoder. The
 * registry's `code` list is the other half of the contract: a key is only
 * offered when there is a line for it here.
 */
export function encoderOptions(symbology, payload, code = {}) {
	const spec = SYMBOLOGIES[symbology] || {};
	const opts = {
		text: payload,
		// Scale 1 makes the emitted coordinate space the module grid itself,
		// which is the whole basis of the physical-size arithmetic below.
		scale: 1,
		// Quiet zone is applied by US, in page units, around the symbol —
		// bwip-js's own padding is in its scaled pixels and would drift.
		includetext: code.text === 'below' || code.text === 'above',
		...(code.text === 'above' ? { textyalign: 'above' } : {}),
	};
	// NO `parse`. It is not what makes `(01)` AI syntax work — the gs1*
	// encoders read that identically with and without it — and what it DOES
	// add is BWIPP's `^NNN` character escape, which silently rewrites any
	// value holding a caret. A lot number of '^0653' encoded as a
	// one-character lot of 'A', in a symbol the same width as the right one.
	const owns = key => (spec.code || []).includes(key);

	// QR error correction: the letters, on the symbologies that take letters.
	if (owns('eclevel') && code.eclevel) opts.eclevel = code.eclevel;
	// Aztec error correction: a NUMBER, the percentage of the symbol given
	// to error correction. BWIPP throws `invalidOptionType` on a letter.
	if (owns('ecpercent') && num(code.ecpercent) !== null) opts.eclevel = code.ecpercent;
	// PDF417 error correction: a NUMBER, 0-8, under the same BWIPP name.
	if (owns('eclevel417') && num(code.eclevel417) !== null) opts.eclevel = code.eclevel417;

	// `version` in whichever vocabulary this symbology speaks. It is already
	// the right shape — the API validates it against the same descriptor —
	// so it only has to reach BWIPP as a string.
	if (owns('version') && code.version !== undefined && code.version !== null && code.version !== '') {
		opts.version = String(code.version);
	}
	// Data Matrix `format`: a symbol SHAPE.
	if (owns('shape') && code.shape && code.shape !== 'auto') opts.format = code.shape;
	if (owns('mask') && num(code.mask) !== null) opts.mask = code.mask;
	if (owns('layers') && num(code.layers) !== null) opts.layers = code.layers;
	if (owns('columns') && num(code.columns) !== null) opts.columns = code.columns;
	if (owns('rows') && num(code.rows) !== null) opts.rows = code.rows;
	// Compact PDF417 — a boolean, the only compaction lever BWIPP has.
	if (owns('compact') && code.compact === true) opts.compact = true;

	// The check digit. `includecheck` turns it on everywhere; MSI is the one
	// symbology with a CHOICE of algorithm, and the choice changes the
	// printed number (mod1010 and mod1110 append two digits, not one), so it
	// has to reach BWIPP under its own name.
	if (owns('checkdigit') && code.checkdigit && code.checkdigit !== 'none') {
		opts.includecheck = true;
		if (symbology === 'msi') opts.checktype = code.checkdigit;
	}
	return opts;
}

/** Is this a quarter turn — the rotations that swap the axes? */
export const isTurned = rotate => rotate === 'R' || rotate === 'L';

/**
 * Pull the module grid out of the SVG bwip-js just emitted, converting its
 * viewBox units to modules with the family's factor.
 *
 * BWIP-JS HAS ALREADY ROTATED IT. Given `rotate: 'R'` it emits the symbol
 * turned and a viewBox with the axes swapped — a Code 128 that is 101x73
 * upright comes back 73x101. So the grid has to be turned BACK here before
 * anything counts modules with it: 73 is the bar height in bwip units and
 * has nothing to do with the bar count. (Reading it as the module count is
 * how a rotated label ends up printed at half its X-dimension, with the
 * error growing as the payload gets longer — and with no error anywhere,
 * because the arithmetic is self-consistent and simply wrong.)
 */
export function gridOf(svg, family = 'matrix', rotate = 'N') {
	const per = unitsPerModule(family);
	const match = /viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/.exec(svg || '');
	if (!match) return { modulesWide: 0, modulesHigh: 0, viewBox: null };
	const vbWidth = Number(match[1]);
	const vbHeight = Number(match[2]);
	const [across, down] = isTurned(rotate) ? [vbHeight, vbWidth] : [vbWidth, vbHeight];
	return {
		modulesWide: across / per,
		modulesHigh: down / per,
		viewBox: { width: vbWidth, height: vbHeight },
	};
}

const round = (value, unit) => Number(Number(value).toFixed(unit === 'mm' ? 3 : 5));

/** bwip-js viewBox units per module, by family. See the note at the top. */
export const unitsPerModule = family => (family === 'matrix' ? 2 : 1);

/**
 * The physical size of a symbol, given its module grid. Pure arithmetic, no
 * encoder: this is the half worth unit-testing, and it is the half the
 * editor needs before anything has been encoded.
 *
 * The quiet zone is counted in MODULES. `boxHeight` is the extent along the
 * BAR-HEIGHT axis — the block's height upright, its width on a quarter turn
 * — and is what a LINEAR symbol takes; pass it only for one.
 */
export function physicalSize({ modulesWide, modulesHigh, module: moduleSize, quiet = 0, unit = 'in', rotate = 'N', family = 'matrix', boxHeight = null }) {
	const across = (Number(modulesWide) || 0) + 2 * quiet;
	const down = family === 'linear' && boxHeight !== null
		? null
		: (Number(modulesHigh) || 0) + 2 * quiet;

	const alongWidth = round(across * moduleSize, unit);
	const alongHeight = down === null ? round(Number(boxHeight) || 0, unit) : round(down * moduleSize, unit);
	// A quarter turn swaps the axes; a half turn does not. The grid handed
	// in is always the UPRIGHT one — see gridOf.
	const turned = isTurned(rotate);
	return {
		width: turned ? alongHeight : alongWidth,
		height: turned ? alongWidth : alongHeight,
		modulesAcross: across,
		modulesDown: down,
	};
}

/** The unit's own last-place tolerance, so a rounded equality is an equality. */
const fitSlack = unit => (unit === 'mm' ? 0.0011 : 0.000011);

/**
 * rMQR HAS NO AUTOMATIC SIZE. BWIPP raises `qrcodeRMQRwithoutVersion` when
 * no version is given and `qrcodeInvalidRMQRversion` for anything that is
 * not one of its thirty-two 'R{rows}x{cols}' names — so an rMQR block with
 * the version left alone could never print at all, and the integer versions
 * an editor would naturally offer could never print either.
 *
 * Rather than make the option mandatory, the smallest symbol the value fits
 * is found the only way there is: by asking the encoder, smallest first.
 * (Thirty-two candidates at worst, a few milliseconds; a fitting version is
 * usually found in the first dozen.)
 */
const RMQR_BY_AREA = [...RMQR_VERSIONS.values].sort((a, b) => {
	const area = v => { const [r, c] = v.slice(1).split('x'); return Number(r) * Number(c); };
	return area(a) - area(b) || a.localeCompare(b);
});

function encodeRmqrAuto(render, opts, drawing) {
	let last = null;
	for (const version of RMQR_BY_AREA) {
		try {
			return render({ ...opts, version }, drawing());
		} catch (e) {
			last = e;
		}
	}
	throw last || new Error('This value is too long for any rMQR symbol.');
}

/**
 * encode({ symbology, payload, module, quiet, rotate, code, unit, box })
 *   -> { svg, width, height, modulesWide, modulesHigh }  |  { error }
 *
 * `width`/`height` are in `unit` and are what the renderer puts straight
 * into CSS. `error` is a sentence for a reader, not a stack.
 *
 * `box` is the BLOCK the symbol has to live in, `{ w, h }` in the same unit.
 * It is not decoration: a linear symbol takes its bar height from it, and a
 * symbol WIDER than it is refused here rather than left to CSS. The API
 * cannot make that call — the module COUNT depends on the item, so a
 * template that fits the author's preview record overflows on the long
 * values later in the run — and the two things CSS would do with the
 * overflow are both silent. Shrinking it prints below the X-dimension floor
 * the API refused at write time; clipping it prints a partial symbol. Both
 * look like a barcode and decode as nothing.
 */
export async function encode({
	symbology,
	payload,
	module: moduleSize,
	quiet,
	rotate = 'N',
	code = {},
	unit = 'in',
	box = null,
}) {
	const spec = SYMBOLOGIES[symbology];
	if (!spec) return { error: `"${symbology}" is not a symbology this platform prints.` };

	// A QR code of a record's bare short URL carries it in capitals, which QR's
	// alphanumeric mode packs smaller (utils/shareUrls qrValue; KPE-002). Any
	// other payload, and every other symbology, is encoded exactly as written.
	const written = String(payload == null ? '' : payload);
	const text = spec.bcid === 'qrcode' ? qrValue(written) : written;
	if (!text.trim()) return { error: 'Nothing to encode — the payload resolved to nothing.' };

	const size = num(moduleSize) === null ? moduleFloor(symbology, unit) : moduleSize;
	const zone = num(quiet) === null ? defaultQuiet(symbology) : quiet;
	const floor = moduleFloor(symbology, unit);
	if (size < floor) {
		return { error: `A ${size}${unit} module is below the ${floor}${unit} this symbol can be printed at — the bars round away.` };
	}

	// A quarter turn puts the bar-height axis across the block's WIDTH, so
	// that is the extent a rotated linear symbol stretches to.
	const turned = isTurned(rotate);
	const barExtent = box ? (turned ? num(box.w) : num(box.h)) : null;
	if (spec.family === 'linear' && barExtent !== null) {
		const barFloor = barHeightFloor(unit);
		if (barExtent < barFloor) {
			const edge = turned ? 'wide' : 'tall';
			return { error: `This block is ${barExtent}${unit} ${edge}, so the bars would be — under the ${barFloor}${unit} a reader needs to sweep them.` };
		}
	}

	// The decidable rules first, so the common mistakes get a sentence that
	// names the problem rather than whatever BWIPP throws.
	const problem = checkPayload(symbology, text);
	if (problem) return { error: problem };

	let encoders;
	try {
		encoders = await loadEncoders();
	} catch (e) {
		return { error: 'The barcode encoder could not be loaded.' };
	}

	const render = encoders[spec.bcid];
	if (typeof render !== 'function') return { error: `"${symbology}" is not a symbology this platform prints.` };

	const opts = encoderOptions(symbology, text, code);
	let svg;
	try {
		svg = spec.autoVersion && opts.version === undefined
			? encodeRmqrAuto(render, { ...opts, rotate }, () => encoders.drawingSVG())
			: render({ ...opts, rotate }, encoders.drawingSVG());
	} catch (e) {
		// BWIPP's messages are already aimed at a person ("bwipp.ean13badLength:
		// EAN-13 must be 12 or 13 digits"), so the useful half is kept and the
		// module prefix dropped.
		const message = (e && e.message ? String(e.message) : '').replace(/^bwipp\.[A-Za-z0-9#]+:\s*/, '');
		return { error: message || 'This value cannot be encoded in this symbology.' };
	}

	const { modulesWide, modulesHigh } = gridOf(svg, spec.family, rotate);
	if (!modulesWide || !modulesHigh) return { error: 'The encoder produced nothing to print.' };

	// A LINEAR symbol's bars may stretch to the box height — bar height is
	// sweep tolerance, not data. Everything else keeps its aspect, because a
	// matrix code pulled out of square is unreadable. bwip-js emits no
	// preserveAspectRatio, and the SVG default (xMidYMid meet) would
	// letterbox a linear symbol inside its own box.
	const stretch = spec.family === 'linear';
	svg = svg.replace('<svg ', `<svg preserveAspectRatio="${stretch ? 'none' : 'xMidYMid meet'}" `);

	const { width, height } = physicalSize({
		modulesWide, modulesHigh, module: size, quiet: zone, unit, rotate,
		family: spec.family, boxHeight: barExtent,
	});

	if (box) {
		const slack = fitSlack(unit);
		const overWide = num(box.w) !== null && width > box.w + slack;
		const overTall = num(box.h) !== null && height > box.h + slack;
		if (overWide || overTall) {
			const need = overWide ? `${width}${unit} wide` : `${height}${unit} tall`;
			const got = overWide ? `${box.w}${unit}` : `${box.h}${unit}`;
			return {
				error: `This value needs a symbol ${need} and the block is ${got}. Give it more room, or a finer X-dimension.`,
			};
		}
	}

	return {
		svg, width, height, modulesWide, modulesHigh,
		module: size, quiet: zone, family: spec.family, stretch, turned,
	};
}
